import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Fade,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Confetti from "react-confetti";
import useWindowSize from "react-use/lib/useWindowSize";

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const navigate = useNavigate();
  const { width, height } = useWindowSize();

  useEffect(() => {
    if (!orderId) {
      setError(true);
      setLoading(false);
      return;
    }

    axios
      .get(`https://sakhisshop.com/api/orders/details/${orderId}`)
      .then((response) => {
        if (response.data && response.data.orderId) {
          setOrder(response.data);
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [orderId]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(to right, #e3f2fd, #f0f4c3)",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box
        sx={{
          height: "100vh",
          background: "linear-gradient(to right, #fce4ec, #f3e5f5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          px: 2,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          ❌ Failed to retrieve order details. Please try again.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/")}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(to right, #f0f4c3, #e3f2fd)",
        py: 6,
        px: 2,
      }}
    >
      <Confetti
        width={width}
        height={height}
        recycle={false}
        numberOfPieces={300}
      />

      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <Card
            sx={{
              boxShadow: 8,
              borderRadius: 4,
              backgroundColor: "#ffffffee",
              border: "2px solid #4caf50",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ textAlign: "center", p: { xs: 3, md: 5 } }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: { xs: 80, md: 100 }, color: "green", mb: 2 }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: "green",
                  fontWeight: "bold",
                  mb: 3,
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                }}
              >
                🎉 Order Placed Successfully!
              </Typography>

              <Typography variant="h6" sx={{ mb: 1 }}>
                📦 Order ID: <strong>{order.orderId}</strong>
              </Typography>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Typography variant="body1">
                  <LocalShippingIcon sx={{ mr: 1, color: "#2196f3" }} />
                  Your order is being processed. Delivery updates will follow
                  soon!
                </Typography>
                <Typography variant="body1">
                  <LocationOnIcon sx={{ mr: 1, color: "#f44336" }} />
                  <strong>Address:</strong> {order.address}
                </Typography>
                <Typography variant="body1">
                  <PaymentIcon sx={{ mr: 1, color: "#ff9800" }} />
                  <strong>Payment:</strong> {order.paymentMethod}
                </Typography>
                <Typography variant="body1">
                  💰 <strong>Pending Payment:</strong> ₹
                  {order.totalAmount.toFixed(2)}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  ✅ Thanks for shopping with <strong>{order.shopName}</strong>!
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                startIcon={<ShoppingCartIcon />}
                sx={{
                  mt: 4,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  px: 5,
                  py: 1.8,
                  borderRadius: 3,
                  boxShadow: "0px 6px 14px rgba(0,0,0,0.2)",
                  background: "linear-gradient(45deg, #4caf50, #81c784)",
                  "&:hover": {
                    background: "linear-gradient(45deg, #43a047, #66bb6a)",
                  },
                }}
                onClick={() => navigate("/customer-dashboard")}
              >
                Continue Shopping
              </Button>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default OrderSuccess;
